import * as React from 'react'
import clx from 'classnames'
import { useNavigate, useParams } from 'react-router-dom'
import { FaIcon, faTriangleExclamationSolid } from '@/lib/fa'
import { Card } from '@/app/features/common/Card'
import { getViolationConfig } from '@/app/features/utils/violations'
import { IconWithBadge } from '@/app/features/common/IconWithBadge'
import { Violation } from '../types'
import { DRIVER_ROUTES } from '@/modules/drivers/routes-typed'
import { Dayjs } from 'dayjs'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { SkeletonBox } from '@/app/features/common/SkeletonBox'
import { Loader } from '@/ui'
import { EmptyStateCardContent } from './EmptyStateCard'

const VIOLATION_DESCRIPTION_KEYS: Record<string, string> = {
  'Brak wpisu kodu kraju na końcu dnia': 'violationDescription.end-place',
  'Brak wymaganych danych (kodu kraju i aktywności)':
    'violationDescription.missing-data-start-end-place-no-data',
  'Brak wymaganych danych (kodu kraju)': 'violationDescription.start-place',
  'Błędny zapis na karcie (jazda)': 'violationDescription.manual-driving',
  'Niespełnienie wymogu ręcznego wprowadzenia danych (brak danych)':
    'violationDescription.no-data-activity',
  'Niespełnienie wymogu ręcznego wprowadzenia danych (brak kodu kraju)':
    'violationDescription.no-data-activity-country',
  'Nieudzielenie prawidłowej przerwy po 6h pracy':
    'violationDescription.daily-working-time-without-break',
  'Odpoczynek dzienny rozpoczęty za późno':
    'violationDescription.dayli-rest-received-too-late',
  'Odpoczynek dzienny za krótki': 'violationDescription.daily-rest-to-short',
  'Odpoczynek tygodniowy rozpoczęty za późno':
    'violationDescription.week-rest-time-distance',
  'Odpoczynek tygodniowy za krótki': 'violationDescription.week-rest-time',
  'Odpoczynek tygodniowy za krótki - brak rekompensaty':
    'violationDescription.no-week-rest-recompense',
  'Przekroczono 48 godzinny tygodniowy wymiar czasu pracy':
    'violationDescription.average-week-work-time-exceeded',
  'Przekroczono czas 10h pracy w dobie':
    'violationDescription.daily-work-time-exceeded',
  'Przekroczono czas 10h pracy w dobie w przypadku pracy w porze nocnej':
    'violationDescription.night-time-work',
  'Przekroczono dopuszczalny czas jazdy dwutygodniowej':
    'violationDescription.two-week-driving-time',
  'Przekroczono dopuszczalny czas jazdy dziennej':
    'violationDescription.daily-driving-time',
  'Przekroczono dopuszczalny czas jazdy tygodniowej':
    'violationDescription.one-week-driving-time',
  'Przekroczono maksymalny 60 godzinny tygodniowy wymiar czasu pracy':
    'violationDescription.extended-week-work-time-exceeded',
  'Przekroczony czas jazdy bez przerwy':
    'violationDescription.driving-without-break',
  'Niepoprawne operowanie przełącznikiem tachografu umożliwiającym zmianę rodzaju aktywności kierowcy':
    'violationDescription.tachograph-chart-switch',
  'Niezgodność oznaczenia czasowego na wykresówce z urzędowym czasem kraju rejestracji pojazdu':
    'violationDescription.tachograph-chart-time-mismatch',
  'Używanie wykresówki powyżej okresu, na jaki jest przeznaczona, wraz z utratą danych':
    'violationDescription.tachograph-chart-day-exceeded',
}

type Props = {
  violations?: Violation[] | null
  className?: string
  date: string
  selectedMonth: Dayjs
  isLoading?: boolean
  isRefreshing?: boolean
}

const ViolationsSkeleton = () => (
  <div className="tw-flex tw-flex-col">
    {[0, 1, 2, 3].map((row) => (
      <div
        key={row}
        className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-b tw-border-base-300 tw-py-3 first:tw-pt-0 last:tw-border-none last:tw-pb-0"
      >
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2">
          <SkeletonBox className="tw-h-4 tw-w-20 tw-shrink-0 tw-rounded-full" />
          <SkeletonBox className="tw-h-3.5 tw-w-full tw-max-w-64" />
        </div>
        <SkeletonBox className="tw-h-3.5 tw-w-16 tw-shrink-0" />
      </div>
    ))}
  </div>
)

export const ViolationsCard = ({
  violations,
  className,
  date,
  selectedMonth,
  isLoading,
  isRefreshing,
}: Props) => {
  const { companyId } = useParams<{ companyId: string }>()
  const navigate = useNavigate()
  const { t } = useLocalize()

  const formatNumberWithSpaces = (value: number): string => {
    const [intPart, decimalPart] = value.toFixed(2).split('.')
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    return `${formattedInt},${decimalPart}`
  }

  const handleOpenAll = () => {
    if (!companyId) return
    const from = selectedMonth.startOf('month').format('YYYY-MM-DD')
    const to = selectedMonth.endOf('month').format('YYYY-MM-DD')
    navigate(
      DRIVER_ROUTES.groupViolationsNew.buildPath({
        companyId,
        from,
        to,
      }),
    )
  }

  const loadingOverlay = isRefreshing ? (
    <Loader
      position="absolute"
      className="tw-inset-0 tw-z-10 tw-bg-base-100/60 tw-backdrop-blur-[1px]"
      size="sm"
    />
  ) : null

  if (isLoading) {
    return (
      <Card
        className={clx('tw-relative', className)}
        paddingBottom={false}
        title={t('dashboard.most-common-violations')}
        action={handleOpenAll}
        classNameContent="!tw-flex !tw-flex-1 !tw-flex-col !tw-overflow-y-auto !tw-p-4 !tw-pt-4"
      >
        <ViolationsSkeleton />
      </Card>
    )
  }

  if (
    !violations ||
    (Array.isArray(violations) && violations.length === 0)
  ) {
    return (
      <Card
        className={clx('tw-relative', className)}
        paddingBottom={false}
        title={t('dashboard.most-common-violations')}
        action={handleOpenAll}
        classNameContent="!tw-overflow-y-auto tw-flex tw-items-center tw-justify-center !tw-p-0"
      >
        {loadingOverlay}
        <EmptyStateCardContent
          description={`${t('dashboard.no-violations-for')} ${date}`}
          icon={
            <FaIcon
              icon={faTriangleExclamationSolid}
              className="tw-h-12 tw-w-12 tw-text-neutral"
            />
          }
        />
      </Card>
    )
  }

  return (
    <Card
      className={clx('tw-relative', className)}
      paddingBottom={false}
      title={t('dashboard.most-common-violations')}
      action={handleOpenAll}
      classNameContent="!tw-flex !tw-flex-1 !tw-flex-col !tw-overflow-y-auto !tw-p-4 !tw-pt-4"
    >
      {loadingOverlay}
      {violations?.length ? (
        <div className="tw-flex tw-flex-col">
          {violations.slice(0, 4).map((violation) => {
            const config = getViolationConfig(violation.type)
            if (!config) return null
            return (
              <div
                key={violation.id}
                className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-b tw-border-base-300 tw-py-3 tw-text-sm first:tw-pt-0 last:tw-border-none last:tw-pb-0"
              >
                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-1">
                  {config.type === 'exclamation' ? (
                    <div className="tw-inline-flex tw-min-w-max tw-flex-shrink-0 tw-items-center tw-gap-0.5">
                      <span className="tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-text-sm tw-font-extrabold tw-leading-none">
                        !
                      </span>
                      <span>{`${violation.count} x ${violation.type}`}</span>
                    </div>
                  ) : (
                    <IconWithBadge
                      className={clx(
                        'tw-mr-1 tw-flex-shrink-0 tw-text-sm',
                        config.className,
                      )}
                      shape={config.shape}
                      variant={config.variant}
                      badgeContent={`${violation.count} x ${violation.type}`}
                    />
                  )}
                  <span className="tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
                    {VIOLATION_DESCRIPTION_KEYS[violation.description]
                      ? t(VIOLATION_DESCRIPTION_KEYS[violation.description])
                      : violation.description}
                  </span>
                </div>
                <span className="tw-flex-shrink-0 tw-whitespace-nowrap">
                  {formatNumberWithSpaces(violation.totalPenalty)}
                </span>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-gap-3">
          <span>
            <FaIcon
              icon={faTriangleExclamationSolid}
              className="tw-h-8 tw-w-8 tw-text-neutral"
            />
          </span>
          <p className="tw-mb-0 tw-text-sm tw-text-content-base/90">
            {t('dashboard.no-violations-for')} {date}
          </p>
        </div>
      )}
    </Card>
  )
}
